import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { IconPlayerPlayFilled } from "@tabler/icons-react"
import SocialProof from "@/components/common/SocialProof"
import Countdown from "@/components/common/Countdown"
import { useTranslation } from "react-i18next"
import { usePlans } from "@/hooks/use-plans"
import { usePromoCode, getPromoWithNearestExpiry } from "@/hooks/use-promo-code"
import { useAuth } from "@/lib/auth-context"
import { useNavigate } from "react-router-dom"
import { getLanguagePath, formatDiscount } from "@/lib/utils"
import { shouldShowCountdown } from "@/lib/countdown"

interface HeroProps {
  onPlayVideo: () => void;
  onPurchase: (planId: string) => void;
}

const Hero = ({ onPlayVideo, onPurchase }: HeroProps) => {
  // @ts-ignore
  const { t, i18n } = useTranslation();
  const { plans, isLoading } = usePlans();
  const { eventCode, eventData, referralCode, referralData } = usePromoCode();
  const { user } = useAuth();
  const navigate = useNavigate();

  const annualPlan = plans.find(plan => plan.id === 'annual');

  const countdownData = getPromoWithNearestExpiry(eventData, referralData);

  const trialPeriodDays = annualPlan?.pricing.trialPeriodDays;
  const countdownTargetDate = countdownData?.expires_at
    ? new Date(countdownData.expires_at * 1000)
    : null;

  const handleProfileClick = () => {
    if (user) {
      // Store the current plan for the confirm account flow
      localStorage.setItem('pendingPurchasePlanId', 'annual');
      navigate(getLanguagePath('confirm-account'));
    }
  };

  const getInitials = (email: string | undefined) => {
    if (!email) return 'U';
    // @ts-ignore
    return email.split('@')[0].slice(0, 2).toUpperCase();
  };

  const getBadgeText = (): string | null => {
    if (eventData?.expires_at && Date.now() / 1000 > eventData.expires_at) {
      return t("promo.WEBSITE.subtitle") as string;
    }

    const code = eventCode || 'WEBSITE';
    const promoKey = `promo.${code}.subtitle`;
    // @ts-ignore
    let translation = t(promoKey) as string;

    // If translation doesn't exist (returns the key itself), return discount text
    if (translation === promoKey) {
      return t(annualPlan?.pricing.discount?.label || '') + ' ' + formatDiscount(annualPlan?.pricing.discount?.percentage || 0, i18n.language)
    }

    const discount = formatDiscount(annualPlan?.pricing.discount?.percentage || 0, i18n.language)
    translation = translation.replace('{{discount}}', discount);

    return translation;
  };

  return (
    <section className="relative min-h-[90vh] md:min-h-[85vh] flex items-end md:items-center justify-center overflow-hidden bg-slate-900">
      {/* User Profile - Top Right */}
      {user && (
        <div className="absolute top-6 right-6 z-30">
          <button
            onClick={handleProfileClick}
            className="flex items-center gap-3 bg-white/10 hover:bg-white/20 backdrop-blur-sm border border-white/20 text-white px-4 py-2 rounded-full transition-all duration-200 group"
          >
            <div className="w-8 h-8 bg-gradient-to-br from-blue-400 to-purple-500 rounded-full flex items-center justify-center text-white text-sm font-semibold">
              {user.user_metadata?.avatar_url ? (
                <img
                  src={user.user_metadata.avatar_url}
                  alt="Profile"
                  className="w-8 h-8 rounded-full object-cover"
                />
              ) : (
                <span>{getInitials(user?.email)}</span>
              )}
            </div>
            <div className="hidden md:flex flex-col items-start">
              <span className="text-sm font-medium">
                {user.user_metadata?.full_name || user.email?.split('@')[0]}
              </span>
              <span className="text-xs text-white/70">
                {/* @ts-ignore */}
                {t('common.change')}
              </span>
            </div>
          </button>
        </div>
      )}

      {/* Video Background */}
      <div className="absolute inset-0 z-0">
        <div className="absolute inset-0 bg-gradient-to-b from-black/0 via-black/20 to-black/90 md:bg-black/50 z-10"></div>
        <div className="flex flex-row w-full h-full relative">
          <div className="hidden md:block flex-grow"></div>
          <div className="hidden md:flex w-full md:w-1/2 items-center justify-center relative">
            <video
              autoPlay
              muted
              loop
              playsInline
              className="w-full h-full object-cover"
            >
              <source src={t('hero.video')} type="video/mp4" />
            </video>
            <div className="absolute bottom-0 h-[92%] md:h-full md:inset-y-0 md:left-0 md:w-48 bg-gradient-to-t md:bg-gradient-to-r from-slate-900 to-slate-900/0"></div>
          </div>
          <div className="flex md:hidden w-full md:w-1/2 items-center justify-center relative">
            <img src={t('assets.heroImage')} alt="Hero" className="w-full h-full object-cover" />
            <div className="absolute bottom-0 h-[92%] md:h-full md:inset-y-0 md:left-0 md:w-48 bg-gradient-to-t md:bg-gradient-to-r from-slate-900 to-slate-900/0"></div>
          </div>
        </div>
      </div>

      {/* Hero Content */}
      <div className="relative z-20 container mx-auto px-4 py-20 md:py-20">
        <div className="grid md:grid-cols-3 lg:grid-cols-3 gap-8 md:gap-12 items-center">
          {/* Left Content */}
          <div className="text-white text-left px-2 sm:px-6 md:pr-0 md:pl-4 md:col-span-2 lg:col-span-2 lg:pl-32">
            <Badge className={`font-regular text-xs md:text-sm mb-0 md:mb-2 !bg-slate-900/30 text-yellow-300 border border-yellow-300 px-4 py-1`}>
              <span className={`transition-all duration-200 ${isLoading ? 'opacity-0' : 'opacity-100'}`}>
                {getBadgeText()}
              </span>
            </Badge>
            <h1 className="font-semibold mt-3 mb-3 leading-loose">
              <div className="text-3xl md:text-5xl leading-snug md:leading-tight">
                {t('hero.title')}
              </div>
              { // @ts-ignore - Type instantiation depth limitation
                t('hero.subtitle') && t('hero.subtitle').trim() !== "" && (
                  <div className="text-sky-300 text-xl md:text-4xl mt-2 md:mt-6">
                    {t('hero.subtitle')}
                  </div>
                )}
              {t('hero.title2') && (
                <div className="text-base md:text-3xl mt-3 leading-snug md:leading-tight font-normal text-slate-300">
                  {t('hero.title2')}
                </div>
              )}
            </h1>

            <div className="mt-4 md:mt-8 mb-0 md:mb-12">
              <SocialProof />
              {countdownTargetDate && countdownData?.valid && shouldShowCountdown(countdownTargetDate) && (
                <div className="mt-4">
                  <Countdown targetDate={countdownTargetDate} promoCode={eventCode || referralCode} />
                </div>
              )}
              {referralCode && (annualPlan?.pricing as any)?.referralSavings != null && (annualPlan?.pricing as any).referralSavings > 0 && !isLoading && (
                <div className="flex md:hidden flex-row items-center gap-4 text-xs mt-2">
                  <span className="text-slate-400">
                    {/* @ts-ignore */}
                    {t('common.referralCodeApplied', {
                      code: referralCode,
                      currency: annualPlan?.pricing.currency,
                      amount: Number((annualPlan as any).pricing.referralSavings).toLocaleString()
                    })}
                  </span>
                </div>
              )}
            </div>

            <div className="hidden md:flex flex-col md:flex-row items-center gap-4">
              <Button
                size="lg"
                className="bg-yellow-300 text-base text-slate-800 hover:bg-yellow-400 transition-colors py-6 rounded-xl border-b-4 border-yellow-500"
                onClick={() => {
                  onPurchase('annual');
                }}
              >
                {trialPeriodDays
                  ? t('purchase.cta.startTrial', { days: trialPeriodDays })
                  : (
                    <>
                      <span>{t('hero.pricing.subscribe')}</span><span className={`transition-all duration-200 ${isLoading ? 'opacity-0' : 'opacity-100'}`}>{`${annualPlan?.pricing.currency}${Number(annualPlan?.pricing.current).toLocaleString()}`}</span>
                    </>
                  )}
              </Button>

              <div className="flex flex-col items-start justify-center gap-1">
                {trialPeriodDays && (
                  <div className="text-sm text-slate-50">
                    {annualPlan?.pricing.currency} {Number(annualPlan?.pricing.current).toLocaleString()} / {t(annualPlan?.unit)}
                  </div>
                )}
                <div className={`flex items-center gap-2 text-sm transition-all duration-200 ${isLoading ? 'opacity-0' : 'opacity-100'}`}>
                  {annualPlan?.pricing.original && !isLoading && (
                    <span className="line-through text-slate-500">
                      {annualPlan?.pricing.currency} {Number(annualPlan?.pricing.original).toLocaleString()}
                    </span>
                  )}
                  {annualPlan?.pricing.discount && !isLoading && (
                    <span className="text-red-400">
                      {formatDiscount(annualPlan.pricing.discount.percentage, i18n.language)}
                    </span>
                  )}
                </div>

                {referralCode && (annualPlan?.pricing as any)?.referralSavings != null && (annualPlan?.pricing as any).referralSavings > 0 && !isLoading && (
                  <div className="flex flex-col md:flex-row items-center gap-4 text-xs">
                    <span className="text-slate-500">
                      {/* @ts-ignore */}
                      {t('common.referralCodeApplied', {
                        code: referralCode,
                        currency: annualPlan?.pricing.currency,
                        amount: Number((annualPlan as any).pricing.referralSavings).toLocaleString()
                      })}
                    </span>
                  </div>
                )}
              </div>
            </div>

          </div>

          {/* Right Content - Video Button */}
          <div className="hidden md:flex flex-col items-start md:items-center justify-start gap-8 px-4 sm:px-6 lg:px-0">
            <Button
              size="lg"
              className="bg-white/10 hover:bg-white/20 backdrop-blur-sm border border-white/20 text-white px-4 lg:px-8"
              onClick={onPlayVideo}
            >
              <IconPlayerPlayFilled className="w-4 h-4 md:w-5 md:h-5 md:mr-1" />
              <span className="text-xs md:text-base">
                {t('hero.watchVideo')}
              </span>
            </Button>
          </div>
        </div>
      </div>
    </section>
  )
}

export default Hero